import React, { useCallback, useEffect, useRef, useState } from "react";

const MAX_IMAGES = 50;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_VIDEOS = 12;
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const MAX_VIDEO_DURATION_SECONDS = 60;

function formatFileSize(bytes) {
    const normalized = Number(bytes || 0);
    if (normalized < 1024 * 1024) {
        return `${Math.max(0, normalized / 1024).toFixed(1)}KB`;
    }
    return `${(normalized / (1024 * 1024)).toFixed(1)}MB`;
}

function readVideoMetadata(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
            const duration = Number(video.duration);
            if (!Number.isFinite(duration) || duration <= 0) {
                URL.revokeObjectURL(url);
                reject(new Error("동영상 재생 시간을 확인할 수 없습니다."));
                return;
            }
            video.onloadedmetadata = null;
            video.onerror = null;
            resolve({ duration, previewUrl: url });
        };
        video.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("동영상 정보를 읽을 수 없습니다."));
        };
        video.src = url;
    });
}

export default function Step3({ data = {}, update }) {
    const menus = Array.isArray(data.menus) ? data.menus : ["", "", ""];
    const images = Array.isArray(data.images) ? data.images : [];
    const videos = Array.isArray(data.videos) ? data.videos : [];
    const imageFileRef = useRef(null);
    const videoFileRef = useRef(null);
    const [previewMedia, setPreviewMedia] = useState(null);
    const imageBytes = images.reduce(
        (total, image) => total + Number(image?.file?.size || 0),
        0
    );
    const videoBytes = videos.reduce(
        (total, video) => total + Number(video?.file?.size || 0),
        0
    );

    const baseInputStyle = {
        color: "var(--ink-default,#1F2937)",
        borderBottom: "1px solid var(--coolgrey-200,#E3E7EF)",
    };

    const handleMenuChange = (index, value) => {
        const next = [...menus];
        next[index] = value;
        update({ menus: next });
    };

    const handleImageFiles = (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!files.length) return;

        const remain = Math.max(0, MAX_IMAGES - images.length);
        const validFiles = files.filter((file) => {
            if (file.size > MAX_IMAGE_BYTES) {
                alert(`${file.name}: 이미지 파일은 2MB 이하만 등록할 수 있습니다.`);
                return false;
            }
            return true;
        });
        const added = validFiles.slice(0, remain).map((file, index) => ({
            id: `${Date.now()}_image_${index}`,
            url: URL.createObjectURL(file),
            file,
        }));
        update({ images: [...images, ...added] });
    };

    const removeImage = (id) => {
        const target = images.find((image) => image.id === id);
        if (target?.url) URL.revokeObjectURL(target.url);
        update({ images: images.filter((image) => image.id !== id) });
    };

    const handleVideoFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!files.length) return;

        const remain = Math.max(0, MAX_VIDEOS - videos.length);
        const added = [];

        for (const [index, file] of files.slice(0, remain).entries()) {
            const extension = String(file.name || "").toLowerCase().split(".").pop();
            if (
                extension !== "mp4" ||
                !["video/mp4", "application/octet-stream", ""].includes(file.type)
            ) {
                alert(`${file.name}: MP4 동영상만 등록할 수 있습니다.`);
                continue;
            }
            if (file.size > MAX_VIDEO_BYTES) {
                alert(`${file.name}: 동영상 파일은 최대 200MB까지 등록할 수 있습니다.`);
                continue;
            }

            try {
                const { duration, previewUrl } = await readVideoMetadata(file);
                if (duration > MAX_VIDEO_DURATION_SECONDS) {
                    URL.revokeObjectURL(previewUrl);
                    alert(`${file.name}: 동영상 길이는 최대 60초까지 가능합니다.`);
                    continue;
                }
                added.push({
                    id: `${Date.now()}_video_${index}`,
                    url: previewUrl,
                    file,
                    duration,
                    uploadStatus: "pending",
                    uploadProgress: 0,
                    uploadError: "",
                });
            } catch (error) {
                alert(`${file.name}: ${error.message}`);
            }
        }

        if (added.length) update({ videos: [...videos, ...added] });
    };

    const removeVideo = (id) => {
        const target = videos.find((video) => video.id === id);
        if (target?.url) URL.revokeObjectURL(target.url);
        update({ videos: videos.filter((video) => video.id !== id) });
    };

    const onKeyDown = useCallback((event) => {
        if (event.key === "Escape") setPreviewMedia(null);
    }, []);

    useEffect(() => {
        if (!previewMedia) return undefined;
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [previewMedia, onKeyDown]);

    return (
        <div className="flex flex-col gap-8">
            <div>
                <div
                    className="mb-1 text-[20px] font-extrabold md:text-[22px]"
                    style={{ color: "var(--ink-strong,#101828)" }}
                >
                    가게 정보 입력
                </div>
                <p className="text-[14px]" style={{ color: "var(--coolgrey-600,#47526B)" }}>
                    마케팅하고 싶은 주요 메뉴 (최대 3개)
                </p>
            </div>

            {[0, 1, 2].map((index) => (
                <Field key={index} label={`주요 메뉴 ${index + 1}`}>
                    <input
                        className="w-full bg-transparent py-2 outline-none placeholder:opacity-60"
                        style={baseInputStyle}
                        placeholder="주요 메뉴 이름을 입력해주세요"
                        value={menus[index] ?? ""}
                        onChange={(event) => handleMenuChange(index, event.target.value)}
                    />
                </Field>
            ))}

            <MediaSection
                title={`홍보 메뉴 이미지 업로드 (최대 ${MAX_IMAGES}장)`}
                description={`JPG/PNG 권장, 파일당 최대 2MB · 현재 ${formatFileSize(imageBytes)}`}
                canAdd={images.length < MAX_IMAGES}
                onAdd={() => imageFileRef.current?.click()}
            >
                {images.map((image) => (
                    <MediaTile key={image.id} onRemove={() => removeImage(image.id)} removeLabel="이미지 삭제">
                        <img
                            src={image.url}
                            alt="이미지 미리보기"
                            className="h-full w-full cursor-pointer object-cover"
                            onClick={() => setPreviewMedia({ type: "image", url: image.url })}
                        />
                    </MediaTile>
                ))}
            </MediaSection>

            <input
                ref={imageFileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageFiles}
            />

            <MediaSection
                title={`홍보 동영상 업로드 (최대 ${MAX_VIDEOS}개)`}
                description={`MP4만 가능, 파일당 최대 200MB, 최대 60초 · 현재 ${formatFileSize(videoBytes)}`}
                canAdd={videos.length < MAX_VIDEOS}
                onAdd={() => videoFileRef.current?.click()}
            >
                {videos.map((video) => (
                    <div key={video.id} className="w-[64px]">
                        <MediaTile
                            onRemove={() => removeVideo(video.id)}
                            removeLabel="동영상 삭제"
                            removeDisabled={video.uploadStatus === "uploading" || video.uploadStatus === "completed"}
                        >
                            <video
                                src={video.url}
                                muted
                                preload="metadata"
                                className="h-full w-full cursor-pointer object-cover"
                                onClick={() => setPreviewMedia({ type: "video", url: video.url })}
                            />
                        </MediaTile>
                        <VideoUploadProgress video={video} />
                    </div>
                ))}
            </MediaSection>

            <input
                ref={videoFileRef}
                type="file"
                accept="video/mp4,.mp4"
                multiple
                className="hidden"
                onChange={handleVideoFiles}
            />

            {previewMedia && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
                    onClick={() => setPreviewMedia(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="relative max-h-[85vh] max-w-[90vw] overflow-hidden rounded-lg bg-white"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {previewMedia.type === "video" ? (
                            <video
                                src={previewMedia.url}
                                controls
                                autoPlay
                                className="block max-h-[85vh] max-w-[90vw] object-contain"
                            />
                        ) : (
                            <img
                                src={previewMedia.url}
                                alt="원본 이미지"
                                className="block max-h-[85vh] max-w-[90vw] object-contain"
                            />
                        )}
                        <button
                            type="button"
                            className="absolute right-2 top-2 h-8 rounded-md bg-white/90 px-3 text-sm ring-1 ring-black/10"
                            onClick={() => setPreviewMedia(null)}
                        >
                            닫기
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function MediaSection({ title, description, canAdd, onAdd, children }) {
    return (
        <div>
            <div className="mb-2">
                <p className="text-sm font-semibold" style={{ color: "var(--ink-strong,#101828)" }}>
                    {title}
                </p>
                <p className="mt-1 text-xs" style={{ color: "var(--coolgrey-600,#47526B)" }}>
                    {description}
                </p>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                {canAdd && (
                    <button
                        type="button"
                        onClick={onAdd}
                        className="flex h-[64px] w-[64px] items-center justify-center rounded-md border text-black"
                        title="파일 추가"
                    >
                        +
                    </button>
                )}
                {children}
            </div>
        </div>
    );
}

function VideoUploadProgress({ video }) {
    const status = video.uploadStatus || "pending";
    const progress = Number(video.uploadProgress || 0);
    const label = {
        pending: "신청 시 업로드",
        uploading: `${progress}%`,
        completed: "완료",
        failed: "실패",
    }[status] || "대기";
    const barColor = status === "failed"
        ? "bg-red-500"
        : status === "completed"
            ? "bg-emerald-500"
            : "bg-violet-500";

    return (
        <div className="mt-1" title={video.uploadError || label}>
            <div className="h-1 overflow-hidden rounded-full bg-neutral-200">
                <div
                    className={`h-full transition-[width] duration-200 ${barColor}`}
                    style={{ width: `${status === "pending" ? 0 : Math.max(3, progress)}%` }}
                />
            </div>
            <div className={`mt-0.5 truncate text-[9px] ${status === "failed" ? "text-red-600" : "text-neutral-500"}`}>
                {label}
            </div>
        </div>
    );
}

function MediaTile({ children, onRemove, removeLabel, removeDisabled = false }) {
    return (
        <div className="relative h-[64px] w-[64px] overflow-hidden rounded-md border bg-black">
            {children}
            <button
                type="button"
                onClick={onRemove}
                disabled={removeDisabled}
                className="absolute right-1 top-1 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-black text-[12px] leading-none text-white ring-1 ring-black/10 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={removeLabel}
                title="삭제"
            >
                ×
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <p className="mb-2 text-sm font-semibold" style={{ color: "var(--ink-strong,#101828)" }}>
                {label}
            </p>
            {children}
        </div>
    );
}
